/**
 * CardCampaignDialog (08/2026): Karten-Link an alle Kunden ohne Karte senden.
 * Zeigt VOR dem Versand alle Empfänger + Ausgeschlossene (visuelle Prüfung).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CreditCard, Loader2, Send, ShieldOff, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  card_added: { text: '✅ Karte hinterlegt', cls: 'text-emerald-600' },
  declined: { text: '🧾 Rechnung gewählt', cls: 'text-amber-600' },
  card_started: { text: '💳 Karte begonnen', cls: 'text-blue-600' },
  open: { text: '⏳ Offen', cls: 'text-zinc-500' },
};

export const CardCampaignDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setSendResult(null);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/card-campaign/recipients`),
        axios.get(`${API}/card-campaign/requests`),
      ]);
      setRecipients(r1.data.recipients || []);
      setExcluded(r1.data.excluded || []);
      const sel = {};
      (r1.data.recipients || []).forEach((r) => { sel[r.customer_id] = !r.last_sent_at; });
      setSelected(sel);
      setRequests(r2.data || []);
    } catch (e) {
      toast.error('Empfänger konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  const send = async () => {
    if (selectedIds.length === 0) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/card-campaign/send`, { customer_ids: selectedIds });
      setSendResult(r.data);
      toast.success(`Versand abgeschlossen: ${r.data.sent_email} E-Mails, ${r.data.sent_whatsapp} WhatsApp`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="card-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard size={20} /> Karten-Link senden
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-zinc-400" size={28} /></div>
        ) : (
          <div className="space-y-5">
            <p className="text-sm text-zinc-600">
              Der Kunde erhält einen Link (WhatsApp + E-Mail, wo vorhanden) und wählt selbst:
              <strong> Karte sicher bei Payrexx hinterlegen</strong> (automatische Abrechnung) oder
              <strong> weiterhin Rechnung/Karte/TWINT nach dem Training</strong> (wird dann nie mehr angeschrieben).
            </p>

            {/* Empfänger */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm">Empfänger ({selectedIds.length}/{recipients.length} ausgewählt)</h3>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" data-testid="select-all-btn" onClick={() => {
                    const sel = {}; recipients.forEach((r) => { sel[r.customer_id] = true; }); setSelected(sel);
                  }}>Alle</Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelected({})}>Keine</Button>
                </div>
              </div>
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto" data-testid="recipient-list">
                {recipients.length === 0 && <p className="p-4 text-sm text-zinc-500">Keine Empfänger — alle aktiven Kunden haben bereits eine Karte oder haben abgelehnt.</p>}
                {recipients.map((r) => (
                  <label key={r.customer_id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-zinc-50" data-testid={`recipient-row-${r.customer_id}`}>
                    <Checkbox
                      checked={!!selected[r.customer_id]}
                      onCheckedChange={(v) => setSelected((p) => ({ ...p, [r.customer_id]: !!v }))}
                    />
                    <span className="font-medium flex-1">
                      {r.name}
                      {r.last_sent_at && (
                        <span className="ml-2 text-[11px] font-normal text-amber-600" data-testid={`already-sent-${r.customer_id}`}>
                          Link bereits am {new Date(r.last_sent_at).toLocaleDateString('de-CH')} gesendet
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-zinc-500">{r.email || '—'}</span>
                    <span className="text-xs text-zinc-500">{r.phone || '—'}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Ausgeschlossene — visuelle Prüfung */}
            {excluded.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2 flex items-center gap-2 text-zinc-500">
                  <ShieldOff size={15} /> Erhalten KEINEN Link ({excluded.length})
                </h3>
                <div className="border border-dashed rounded-lg divide-y max-h-40 overflow-y-auto bg-zinc-50/50" data-testid="excluded-list">
                  {excluded.map((r) => (
                    <div key={r.customer_id} className="flex items-center gap-3 px-3 py-1.5 text-xs text-zinc-500">
                      <span className="flex-1">{r.name}</span>
                      <span className="italic">{r.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Versand-Ergebnis */}
            {sendResult && (
              <div className="border rounded-lg p-3 bg-emerald-50/50 text-sm" data-testid="send-result">
                <p className="font-semibold mb-1">Versand-Ergebnis: {sendResult.sent_email} E-Mails, {sendResult.sent_whatsapp} WhatsApp{sendResult.skipped > 0 ? `, ${sendResult.skipped} übersprungen` : ''}</p>
                <div className="max-h-32 overflow-y-auto space-y-0.5">
                  {(sendResult.results || []).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {r.skipped ? <XCircle size={12} className="text-amber-500" /> : <CheckCircle2 size={12} className="text-emerald-500" />}
                      <span>{r.name}</span>
                      {!r.skipped && <span className="text-zinc-500">{r.email ? '✉️' : ''}{r.whatsapp ? ' 📱' : ''}</span>}
                      {r.skipped && <span className="text-amber-600 italic">{r.reason}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bisherige Antworten */}
            {requests.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2">Bisherige Anfragen &amp; Antworten</h3>
                <div className="border rounded-lg divide-y max-h-40 overflow-y-auto" data-testid="campaign-requests-list">
                  {requests.map((r) => {
                    const s = STATUS_LABEL[r.display_status] || STATUS_LABEL.open;
                    return (
                      <div key={r.id} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                        <span className="flex-1 font-medium">{r.customer_name}</span>
                        <span className="text-zinc-400">{(r.created_at || '').slice(0, 10)}</span>
                        <span className={s.cls}>{s.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Schliessen</Button>
              <Button
                onClick={send}
                disabled={sending || selectedIds.length === 0}
                data-testid="send-campaign-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {sending ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
                An {selectedIds.length} Kunden senden
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CardCampaignDialog;
